import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PageLayout from "@/components/feature/PageLayout";
import { useSeo } from "@/hooks/useSeo";

const EMAIL = "feedback@latestmovie.example";

const values = [
  {
    icon: "ri-flashlight-line",
    title: "及时",
    desc: "紧跟院线节奏，第一时间整理最新上映与上映动态，减少信息查找成本。",
  },
  {
    icon: "ri-shield-check-line",
    title: "清晰",
    desc: "用统一的信息结构呈现评分、简介与类型，让判断电影是否值得一看变得简单。",
  },
  {
    icon: "ri-heart-line",
    title: "用心",
    desc: "从影迷视角出发打磨内容分类与筛选体验，让找电影这件事本身也愉快。",
  },
];

const timeline = [
  { year: "2024", text: "「最新电影」内容框架搭建，确立资讯 + 评分 + 分类浏览的核心结构。" },
  { year: "2025", text: "上线影院上映动态板块，支持按地区查看影院排片与场次信息。" },
  { year: "2026", text: "完善多维筛选与收藏功能，让每位影迷都能拥有自己的观影清单。" },
];

export default function About() {
  const [toast, setToast] = useState<string | null>(null);

  useSeo({
    title: "关于我们 - 最新电影",
    description:
      "了解「最新电影」平台：我们专注高清电影资讯、电影评分介绍与影院上映动态，致力于帮助影迷更高效地发现值得观看的最新影片。",
    keywords: "关于最新电影,电影资讯平台,电影评分介绍,影视动态,联系我们",
    canonicalPath: "/about",
  });

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 2000);
    return () => clearTimeout(timer);
  }, [toast]);

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(EMAIL);
      setToast("邮箱地址已复制");
    } catch {
      setToast("复制失败，请手动选择邮箱地址");
    }
  };

  return (
    <PageLayout>
      <div className="w-full bg-primary-950">
        <div className="w-full max-w-[1360px] mx-auto px-4 md:px-6 py-12 md:py-16">
          <nav className="flex items-center gap-2 text-xs text-background-400 mb-5">
            <Link to="/" className="hover:text-background-100 transition-colors cursor-pointer">
              首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-background-100">关于我们</span>
          </nav>
          <span className="inline-flex items-center gap-1.5 text-xs text-secondary-300 border border-secondary-300/40 rounded-full px-3 py-1">
            <i className="ri-information-line"></i>
            关于最新电影
          </span>
          <h1 className="mt-5 text-3xl md:text-4xl font-heading font-black text-background-50 leading-tight max-w-3xl">
            我们只想让你更快找到
            <br />
            下一部想看的电影
          </h1>
          <p className="mt-5 text-sm md:text-[15px] text-background-300 max-w-2xl leading-relaxed">
            「最新电影」是一个以高清电影资讯与上映动态为核心的内容平台。我们把最新上映影片、
            评分介绍、类型分类与影院排片整理在一起，希望用清晰的信息结构，帮你把时间花在电影本身，而不是到处查找。
          </p>
        </div>
      </div>

      <div className="w-full max-w-[1360px] mx-auto px-4 md:px-6 py-12 md:py-16">
        {/* 创办宗旨 */}
        <section>
          <div className="flex items-center gap-2.5 mb-6">
            <span className="w-1.5 h-6 rounded-full bg-accent-500"></span>
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">创办宗旨</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
            {values.map((v) => (
              <div key={v.title} className="rounded-lg bg-background-50 border border-background-200 p-6">
                <span className="w-12 h-12 flex items-center justify-center rounded-lg bg-primary-50 text-primary-600">
                  <i className={`${v.icon} text-2xl`}></i>
                </span>
                <h3 className="mt-4 text-lg font-semibold text-foreground-950">{v.title}</h3>
                <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* 发展历程 */}
        <section className="mt-14">
          <div className="flex items-center gap-2.5 mb-6">
            <span className="w-1.5 h-6 rounded-full bg-secondary-500"></span>
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">平台札记</h2>
          </div>
          <div className="rounded-lg bg-background-100 border border-background-200 p-6 md:p-8">
            <div className="relative pl-6 border-l border-background-300 space-y-8">
              {timeline.map((t) => (
                <div key={t.year} className="relative">
                  <span className="absolute -left-[31px] top-1 w-3.5 h-3.5 rounded-full bg-accent-500 ring-4 ring-background-100"></span>
                  <p className="font-display font-bold text-lg text-primary-700">{t.year}</p>
                  <p className="mt-1 text-sm text-foreground-700 leading-relaxed">{t.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 联系方式 */}
        <section className="mt-14">
          <div className="flex items-center gap-2.5 mb-6">
            <span className="w-1.5 h-6 rounded-full bg-accent-500"></span>
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
              联系我们
            </h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div className="rounded-lg bg-primary-950 p-6 md:p-8">
              <span className="w-12 h-12 flex items-center justify-center rounded-lg bg-accent-500 text-background-50">
                <i className="ri-mail-send-line text-2xl"></i>
              </span>
              <h3 className="mt-4 text-background-50 font-semibold text-lg">反馈与建议</h3>
              <p className="mt-2 text-sm text-background-300 leading-relaxed">
                如果你对内容分类、影片信息或使用体验有任何建议，欢迎通过邮件与我们联系，我们会认真阅读每一封来信。
              </p>
              <button
                type="button"
                onClick={copyEmail}
                className="mt-5 inline-flex items-center gap-2 px-5 py-3 rounded-md bg-background-50 text-foreground-950 text-sm font-medium hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-file-copy-line"></i>
                {EMAIL}
              </button>
              <p className="mt-2 text-xs text-background-400">点击即可复制邮箱地址</p>
            </div>

            <div className="rounded-lg bg-background-50 border border-background-200 p-6 md:p-8">
              <h3 className="font-semibold text-lg text-foreground-950">其他渠道</h3>
              <ul className="mt-4 space-y-4">
                <li className="flex items-start gap-3">
                  <span className="w-9 h-9 flex items-center justify-center rounded-md bg-background-100 text-primary-600 shrink-0">
                    <i className="ri-wechat-line text-lg"></i>
                  </span>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">官方账号</p>
                    <p className="text-sm text-foreground-500">搜索「最新电影」即可关注内容更新</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-9 h-9 flex items-center justify-center rounded-md bg-background-100 text-primary-600 shrink-0">
                    <i className="ri-time-line text-lg"></i>
                  </span>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">内容更新</p>
                    <p className="text-sm text-foreground-500">资讯每日更新，上映动态每周整理</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-9 h-9 flex items-center justify-center rounded-md bg-background-100 text-primary-600 shrink-0">
                    <i className="ri-map-pin-line text-lg"></i>
                  </span>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">覆盖范围</p>
                    <p className="text-sm text-foreground-500">影院动态覆盖全国主要城市</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* 免责声明 */}
        <section className="mt-14">
          <div className="flex items-center gap-2.5 mb-6">
            <span className="w-1.5 h-6 rounded-full bg-secondary-500"></span>
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
              版权与免责声明
            </h2>
          </div>
          <div className="rounded-lg border border-background-200 bg-background-100 p-6 md:p-8 space-y-4 text-sm text-foreground-600 leading-relaxed">
            <p>
              1. 本网站目前为影视资讯演示站点，站内所有影片名称、简介、评分、导演与主创信息、影院名称及场次信息
              均为虚构示例或用于版式演示的模拟内容，不对应任何真实影片或个人，仅供参考。
            </p>
            <p>
              2. 本站不提供在线购票、选座或任何形式的票务交易服务，影院相关数据不代表真实排片，请以影院官方渠道为准。
            </p>
            <p>
              3. 网站内容将持续更新完善。若你发现任何信息存在偏差或涉及权益问题，欢迎通过上方邮箱与我们联系处理。
            </p>
            <p>© {new Date().getFullYear()} 最新电影 Latest Movies · 保留所有权利。</p>
          </div>
        </section>

        <div className="mt-12 flex flex-wrap gap-3">
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-accent-500 text-background-50 text-sm font-semibold hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-home-4-line"></i>
            返回首页
          </Link>
          <Link
            to="/cinema"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-md border border-background-300 text-foreground-700 text-sm font-medium hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-store-3-line"></i>
            查看影院动态
          </Link>
        </div>
      </div>

      {toast && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-[60] animate-fade-in-up">
          <div className="flex items-center gap-2 px-5 py-3 rounded-md bg-primary-950 text-background-50 text-sm">
            <i className="ri-checkbox-circle-fill text-secondary-400 text-lg"></i>
            {toast}
          </div>
        </div>
      )}
    </PageLayout>
  );
}